import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/auth/auth'
import * as AuthTypes from '@wtf/shared'
import { User } from 'firebase/auth' // Import User type from firebase/auth
import { auth } from '@/auth/firebase'
import { sendEmailVerification, type ActionCodeSettings } from 'firebase/auth'

/**
 * Hook to register a new user (email/password)
 */
export function useRegisterUser() {
    return useMutation<
        AuthTypes.AuthResponse,
        Error,
        AuthTypes.RegisterUserRequest
    >({
        mutationFn: (data) => authApi.registerUser(data),
        // onSuccess/onError handled in the component for navigation/state updates
    })
}

/**
 * Hook to register a new stylist (email/password)
 */
export function useRegisterStylist() {
    return useMutation<
        AuthTypes.AuthResponse,
        Error,
        AuthTypes.RegisterStylistRequest
    >({
        mutationFn: (data) => authApi.registerStylist(data),
        // onSuccess/onError handled in the component
    })
}

/**
 * Hook to log in a user (email/password)
 */
export function useLogin() {
    const queryClient = useQueryClient()

    return useMutation<User, Error, AuthTypes.LoginRequest>({
        mutationFn: (data) => authApi.login(data),
        onSuccess: () => {
            // Invalidate potentially sensitive user-specific data on login
            queryClient.invalidateQueries({ queryKey: ['collections'] })
            queryClient.invalidateQueries({ queryKey: ['collection'] }) // Invalidate individual collections (may contain sensitive data)
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['looks'] })
            queryClient.invalidateQueries({ queryKey: ['user'] })
            queryClient.invalidateQueries({ queryKey: ['profile'] }) // Invalidate profile data (may contain sensitive info)
            queryClient.invalidateQueries({ queryKey: ['feeds'] }) // Invalidate feeds to refresh from guest feed to personalized feed
        },
    })
}

/**
 * Hook for the Google authentication callback step.
 * Calls the backend `/api/auth/google/callback` endpoint.
 */
export function useGoogleAuthCallback() {
    const queryClient = useQueryClient()

    return useMutation<
        AuthTypes.GoogleAuthCallbackResponse,
        Error,
        AuthTypes.GoogleAuthCallbackRequest
    >({
        mutationFn: (data) => authApi.googleAuthCallback(data),
        onSuccess: (data) => {
            // If login was successful, invalidate user-specific queries
            if (data.status === 'logged_in') {
                queryClient.invalidateQueries({ queryKey: ['collections'] })
                queryClient.invalidateQueries({ queryKey: ['collection'] }) // Invalidate individual collections (may contain sensitive data)
                queryClient.invalidateQueries({ queryKey: ['products'] })
                queryClient.invalidateQueries({ queryKey: ['looks'] })
                queryClient.invalidateQueries({ queryKey: ['user'] })
                queryClient.invalidateQueries({ queryKey: ['profile'] }) // Invalidate profile data (may contain sensitive info)
                queryClient.invalidateQueries({ queryKey: ['feeds'] }) // Invalidate feeds to refresh from guest feed to personalized feed
            }
            // If status is 'pending_registration', the UI should handle the next step
        },
    })
}

/**
 * Hook for completing Google registration.
 * Calls the backend `/api/auth/google/complete-registration` endpoint.
 */
export function useGoogleCompleteRegistration() {
    const queryClient = useQueryClient()

    return useMutation<
        AuthTypes.AuthResponse,
        Error,
        AuthTypes.GoogleCompleteRegistrationRequest
    >({
        mutationFn: (data) => authApi.googleCompleteRegistration(data),
        onSuccess: (_data) => {
            // On successful registration, invalidate user-specific queries
            queryClient.invalidateQueries({ queryKey: ['collections'] })
            queryClient.invalidateQueries({ queryKey: ['collection'] }) // Invalidate individual collections (may contain sensitive data)
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['looks'] })
            queryClient.invalidateQueries({ queryKey: ['user'] })
            queryClient.invalidateQueries({ queryKey: ['profile'] }) // Invalidate profile data (may contain sensitive info)
            queryClient.invalidateQueries({ queryKey: ['feeds'] }) // Invalidate feeds to refresh from guest feed to personalized feed
        },
    })
}

/**
 * Hook for the Apple authentication callback step.
 * Calls the backend `/api/auth/apple/callback` endpoint.
 */
export function useAppleAuthCallback() {
    const queryClient = useQueryClient()

    return useMutation<
        AuthTypes.AppleAuthCallbackResponse,
        Error,
        AuthTypes.AppleAuthCallbackRequest
    >({
        mutationFn: (data) => authApi.appleAuthCallback(data),
        onSuccess: (data) => {
            // If login was successful, invalidate user-specific queries
            if (data.status === 'logged_in') {
                queryClient.invalidateQueries({ queryKey: ['collections'] })
                queryClient.invalidateQueries({ queryKey: ['collection'] })
                queryClient.invalidateQueries({ queryKey: ['products'] })
                queryClient.invalidateQueries({ queryKey: ['looks'] })
                queryClient.invalidateQueries({ queryKey: ['user'] })
                queryClient.invalidateQueries({ queryKey: ['profile'] })
                queryClient.invalidateQueries({ queryKey: ['feeds'] })
            }
        },
    })
}

/**
 * Hook for completing Apple registration.
 * Calls the backend `/api/auth/apple/complete-registration` endpoint.
 */
export function useAppleCompleteRegistration() {
    const queryClient = useQueryClient()

    return useMutation<
        AuthTypes.AuthResponse,
        Error,
        AuthTypes.AppleCompleteRegistrationRequest
    >({
        mutationFn: (data) => authApi.appleCompleteRegistration(data),
        onSuccess: (_data) => {
            // On successful registration, invalidate user-specific queries
            queryClient.invalidateQueries({ queryKey: ['collections'] })
            queryClient.invalidateQueries({ queryKey: ['collection'] })
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['looks'] })
            queryClient.invalidateQueries({ queryKey: ['user'] })
            queryClient.invalidateQueries({ queryKey: ['profile'] })
            queryClient.invalidateQueries({ queryKey: ['feeds'] })
        },
    })
}

/**
 * Hook to log out a user
 */
export function useLogout() {
    const queryClient = useQueryClient()

    return useMutation<void, Error, void>({
        mutationFn: () => authApi.logout(),
        onSuccess: () => {
            // Clear the entire query cache on logout for security
            queryClient.clear()
        },
    })
}

// Add password reset hooks
export function useSendPasswordResetEmail() {
    return useMutation<void, Error, { email: string }>({
        mutationFn: ({ email }) => authApi.forgotPassword({ email }),
    })
}

export function useConfirmPasswordReset() {
    return useMutation<void, Error, { oobCode: string; newPassword: string }>({
        mutationFn: ({ oobCode, newPassword }) =>
            authApi.resetPassword({ oobCode, newPassword }),
    })
}

// Hook to send email verification to the currently authenticated user
export function useSendEmailVerification() {
    return useMutation<void, Error, { continueUrl?: string }>({
        mutationFn: async ({ continueUrl }) => {
            const user = auth.currentUser
            if (!user) {
                throw new Error('User not authenticated')
            }

            // Build the action URL - Firebase will append mode, oobCode, etc.
            // We only include continueUrl if provided, Firebase handles the rest
            const actionUrl = continueUrl
                ? `${window.location.origin}/action?continueUrl=${encodeURIComponent(continueUrl)}`
                : `${window.location.origin}/action`

            const actionCodeSettings: ActionCodeSettings = {
                // Firebase will append ?mode=verifyEmail&oobCode=... to this URL
                url: actionUrl,
                handleCodeInApp: true,
                // These settings help the link open in the PWA instead of browser
                iOS: {
                    bundleId: 'com.style.wtf',
                },
                android: {
                    packageName: 'com.style.wtf',
                    installApp: false, // Don't try to install a native app
                    minimumVersion: '1',
                },
            }
            return sendEmailVerification(user, actionCodeSettings)
        },
    })
}

/**
 * Hook to check if an email already exists
 */
export function useCheckEmailExists() {
    return useMutation<boolean, Error, string>({
        mutationFn: (email: string) => authApi.checkEmailExists(email),
    })
}
